//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import {
  Box,
  ClickAwayListener,
  Grid,
  List,
  ListItem,
  ListItemText,
  Popper,
  PopperPlacementType,
  Typography,
} from '@mui/material'
import ButtonAction from '@react/components/elements/ButtonAction'
import { chainActions } from '@react/redux/chainSlice'
import React, { ReactElement, useEffect } from 'react'

import { AddressPill } from '@react/components/addresses/AddressPill'
import { SectionHeader } from '@react/components/common/SectionHeader'
import PowerIcon from '@react/components/icons/PowerIcon'
import { useOtChainState, useOtDispatch } from '@react/redux/store'
import { ERC20Wei, erc20WeiToCommaDecString } from '@shared/conversions'
import { RoleEnum } from '@shared/Enums'
import { ethers, ZeroAddress } from 'ethers'
import { useNavigate } from 'react-router-dom'

import { logOT } from '@shared/loggingShared'
import { useAppKitAccount, useDisconnect } from '@reown/appkit/react'
import { useAuth } from 'react-oidc-context'
import { Erc20Abi } from '@shared/Abis'
import { ChainAccountValues } from '@shared/LiquidityAsset'

export const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  borderRadius: '20px',
  border: 0,
  boxShadow: '0',
  p: 4,
}

function LogoutButton(): React.ReactElement {
  const dispatch = useOtDispatch()
  const { disconnect } = useDisconnect()
  const {
    stageConfiguration: { domain, cognitoConfig },
  } = useOtChainState()
  const { clientId: cognitoClientId, userPoolId: cognitoUserPoolId } = cognitoConfig ?? {}

  const auth = useAuth()

  async function logoutFromCognito() {
    await dispatch(chainActions.odicDisconnect())

    const logoutPoolIdAdjusted = cognitoUserPoolId?.replace('_', '').toLowerCase()
    const logoutUriAdjusted = `https://${domain.replace('app', 'admin')}`
    const cognitoDomain = `https://${logoutPoolIdAdjusted}.auth.us-east-1.amazoncognito.com`
    const ref = `${cognitoDomain}/logout?client_id=${cognitoClientId}&logout_uri=${encodeURIComponent(
      logoutUriAdjusted
    )}`
    window.location.href = ref
  }
  function logoutAction() {
    logOT.info(' logoutAction start')
    disconnect()
      .then(() => {
        logOT.info('Disconnected from AppKit')
        dispatch(chainActions.logoffUser())
        logOT.info('OdicLoginWrapper calling auth.signoutRedirect')
        if (auth.isAuthenticated) {
          return logoutFromCognito()
        }
      })

      .catch((error) => {
        logOT.error(' LogoutButton error', error)
      })
  }

  return (
    <ButtonAction
      sx={{ marginBottom: 4, backgroundColor: '#F2F2F2' }}
      startIcon={<PowerIcon />}
      variant={'tertiary'}
      onClick={() => {
        logoutAction()
      }}
    >
      Disconnect
    </ButtonAction>
  )
}

export default function AccountButtonMenu(): ReactElement {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const openMenu = Boolean(anchorEl)
  const {
    userConnection: { accountBalances, accountAddr, roleEnum, companyProfile },
    chainConfiguration,
    stageConfiguration,
    cognitoUser,
    chainConfiguration: { chainName, chainId, perimeterAddresses, providerReadOnly },
  } = useOtChainState()

  const navigate = useNavigate()
  const dispatch = useOtDispatch()
  const { disconnect } = useDisconnect()
  const { embeddedWalletInfo } = useAppKitAccount({ namespace: 'eip155' })

  async function fetchAccountBalances() {
    if (perimeterAddresses) {
      const accountBalances: ChainAccountValues = {}
      for (const liquidityAsset of perimeterAddresses.liquidityAssets) {
        const tokenContract = new ethers.Contract(liquidityAsset.addr, Erc20Abi, providerReadOnly)
        const balance = await tokenContract.balanceOf(accountAddr)

        accountBalances[liquidityAsset.addr] = balance.toString()
      }
      dispatch(chainActions.updateAccountBalances(accountBalances))
    }
  }
  const handleClickAway = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
    setAnchorEl(null)
  }
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
    fetchAccountBalances()
  }
  const handleDemoCLick = (): void => {
    navigate('/demo')
    handleClickAway()
  }
  const handleCompanyProfilesClick = (): void => {
    navigate('/companyProfiles')
    handleClickAway()
  }

  const handleReportsCLick = (): void => {
    navigate('/reports')
    handleClickAway()
  }
  const handleDisconnect = (): void => {
    logOT.info('Disconnecting from AppKit')
    disconnect()
    dispatch(chainActions.logoffUser())
  }

  const placement: PopperPlacementType = 'bottom-end'

  if (!chainConfiguration || !accountAddr || accountAddr === ZeroAddress) {
    return <> </>
  }

  return (
    <>
      <ButtonAction variant={'tertiary'} onClick={handleMenuOpen}>
        {(accountAddr ? accountAddr : ZeroAddress).substring(0, 6) + '...'}
      </ButtonAction>
      <Popper sx={{ zIndex: 10 }} id="basic-menu" anchorEl={anchorEl} open={openMenu} placement={placement}>
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box
            sx={{
              marginTop: 6,
              marginRight: 2,
              width: 375,
              height: roleEnum === RoleEnum.BORROWER || roleEnum === RoleEnum.POOL_ADMIN ? 700 : 480,
              border: '0',
              bgcolor: 'white',
              borderRadius: '10px',
              boxShadow: '0 0 5px 0 rgba(0,0,0,0.1)',
              padding: 4,
            }}
          >
            {cognitoUser ? (
              <LogoutButton />
            ) : (
              <ButtonAction
                sx={{ marginBottom: 4, backgroundColor: '#F2F2F2' }}
                startIcon={<PowerIcon />}
                variant={'tertiary'}
                onClick={handleDisconnect}
              >
                Disconnect
              </ButtonAction>
            )}
            <Box sx={{ marginBottom: 4, marginTop: 2 }}>
              <Typography sx={{ color: '#666' }} variant="h6">
                Account
              </Typography>
              <AddressPill style={{ width: '120px', padding: '5px', marginTop: 2 }} addr={accountAddr} />
              {embeddedWalletInfo && (
                <Typography sx={{ fontWeight: 'bold' }} variant="small">
                  {embeddedWalletInfo.user?.email ?? embeddedWalletInfo.user?.username ?? ''}
                </Typography>
              )}
              {companyProfile && (
                <Typography sx={{ fontWeight: 'bold' }} variant="small">
                  {companyProfile.companyName}
                </Typography>
              )}
              {/* <appkit-account-button /> */}
            </Box>
            {cognitoUser && cognitoUser.profile && cognitoUser.profile.email && (
              <Box sx={{ marginBottom: 4, marginTop: 2 }}>
                <Typography sx={{ color: '#666' }} variant="h6">
                  Login Account
                </Typography>
                <Typography sx={{ fontWeight: 'bold' }} variant="small">
                  {cognitoUser.profile.email}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography sx={{ color: '#666' }} variant="h6">
                Chain ID
              </Typography>
              <Typography sx={{ fontWeight: 'bold' }} variant="small">
                {chainName + '  ' + chainId}
              </Typography>
            </Box>
            <Grid sx={{ marginTop: 5 }} key={'Balances'}>
              <SectionHeader>Liquidity Asset Balances</SectionHeader>
            </Grid>
            {Object.entries(accountBalances ?? {}).map(([addr, balance]) => {
              const liquidityAsset = perimeterAddresses?.liquidityAssets.find((lq) => lq.addr == addr)
              return (
                <Box sx={{ marginTop: 4 }} key={addr}>
                  <Typography sx={{ color: '#666' }} variant="h6">
                    {liquidityAsset?.name ?? 'Unknown Asset'}
                  </Typography>
                  <Typography sx={{ fontWeight: 'bold' }} variant="small">
                    {erc20WeiToCommaDecString(balance as ERC20Wei)}
                  </Typography>
                </Box>
              )
            })}

            {roleEnum === RoleEnum.BORROWER || roleEnum === RoleEnum.POOL_ADMIN ? (
              <Grid sx={{ marginTop: 4 }} key={'Admin'}>
                <SectionHeader>Admin tasks</SectionHeader>
              </Grid>
            ) : null}
            {roleEnum === RoleEnum.BORROWER || roleEnum === RoleEnum.POOL_ADMIN ? (
              <Box>
                <List>
                  <ListItem
                    sx={{ margin: 0, padding: 0, textDecoration: 'underline', cursor: 'pointer' }}
                    component="a"
                    onClick={handleReportsCLick}
                  >
                    <ListItemText primary="Reports" />
                  </ListItem>

                  <ListItem
                    sx={{ margin: 0, padding: 0, textDecoration: 'underline', cursor: 'pointer' }}
                    component="a"
                    onClick={handleCompanyProfilesClick}
                  >
                    <ListItemText primary="Company Profiles" />
                  </ListItem>

                  <ListItem
                    sx={{ margin: 0, padding: 0, textDecoration: 'underline', cursor: 'pointer' }}
                    component="a"
                    onClick={handleDemoCLick}
                  >
                    <ListItemText primary="Demo Management" />
                  </ListItem>
                </List>
              </Box>
            ) : null}
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  )
}
